// ═══════════════════════════════════════════════════════════════════════════
// ¿Esta contraseña ya se filtró en alguna brecha?
//
// POR QUÉ ESTÁ ESCRITO AQUÍ. El asesor de seguridad de Supabase recomienda
// encender `password_hibp_enabled`, y al intentarlo contestó:
//
//     Configuring leaked password protection via HaveIBeenPwned.org is
//     available on Pro Plans and up.
//
// O sea que es de paga y los dos proyectos están en el plan gratuito. Pero la
// protección en sí no es de Supabase: es una API pública y gratuita de
// HaveIBeenPwned, y consultarla son treinta líneas. Así que se hace aquí.
//
// ── LA CONTRASEÑA NO SALE DE ESTA MÁQUINA ──────────────────────────────────
//
// Esto es lo importante y por eso va arriba. El protocolo se llama
// *k-anonymity* y funciona así:
//
//   1. se calcula el SHA-1 de la contraseña, aquí, en el navegador
//   2. se mandan **los primeros cinco caracteres** de ese hash, nada más
//   3. el servidor devuelve TODOS los hashes que empiezan igual —cientos— y la
//      comparación final se hace aquí
//
// Con cinco caracteres hexadecimales hay un millón de prefijos posibles y cada
// consulta cae en un grupo de cientos de contraseñas distintas. Ni la
// contraseña ni su hash completo salen nunca. Es el modo diseñado justamente
// para poder preguntar sin delatar.
//
// ── QUÉ PASA SI NO HAY RED ─────────────────────────────────────────────────
//
// Se deja pasar. Es una decisión, no un descuido: la alternativa es que alguien
// sin señal —en el valle, que es donde se trabaja— no pueda poner la contraseña
// que necesita para recuperar su cuenta. Una comprobación de higiene no debe
// convertirse en un candado. Queda dicho aquí para que nadie lo lea como un
// olvido.
// ═══════════════════════════════════════════════════════════════════════════

const API = "https://api.pwnedpasswords.com/range/";

/** SHA-1 en hexadecimal y en mayúsculas, que es como responde el servicio. */
async function sha1(texto) {
  const datos = new TextEncoder().encode(texto);
  const buf = await crypto.subtle.digest("SHA-1", datos);
  return [...new Uint8Array(buf)]
    .map(b => b.toString(16).padStart(2, "0")).join("").toUpperCase();
}

/**
 * Separa el hash como lo pide el protocolo: prefijo que se manda, sufijo que se
 * queda. Va aparte para poder probarlo — es donde un error se vería como "todo
 * bien" en vez de como una falla.
 */
export function partirHash(hash) {
  return { prefijo: hash.slice(0, 5), sufijo: hash.slice(5) };
}

/**
 * Busca el sufijo en la respuesta del servicio.
 *
 * El cuerpo son líneas `SUFIJO:CUANTAS_VECES`. Se compara sin distinguir
 * mayúsculas por si el servicio cambia de formato algún día.
 */
export function vecesEnRespuesta(cuerpo, sufijo) {
  for (const linea of String(cuerpo || "").split("\n")) {
    const [suf, veces] = linea.trim().split(":");
    if (suf && suf.toUpperCase() === sufijo.toUpperCase())
      return parseInt(veces, 10) || 0;
  }
  return 0;
}

/**
 * ¿Aparece en alguna brecha conocida? Devuelve cuántas veces.
 *
 * Nunca lanza: si el servicio no contesta, devuelve `{ veces: 0, medido: false }`
 * y quien llama decide. `medido` existe para poder distinguir "no está filtrada"
 * de "no se pudo preguntar", que son cosas muy distintas y confundirlas sería
 * decirle al usuario una tranquilidad que nadie comprobó.
 */
export async function vecesFiltrada(contrasena) {
  if (!contrasena) return { veces: 0, medido: false };
  try {
    const { prefijo, sufijo } = partirHash(await sha1(contrasena));
    const r = await fetch(API + prefijo, {
      // `Add-Padding` le pide al servicio que rellene la respuesta con hashes
      // falsos, para que su tamaño no insinúe cuántos resultados hubo.
      headers: { "Add-Padding": "true" },
    });
    if (!r.ok) return { veces: 0, medido: false };
    return { veces: vecesEnRespuesta(await r.text(), sufijo), medido: true };
  } catch {
    return { veces: 0, medido: false };   // sin red: se deja pasar
  }
}

/** El aviso que se le enseña a una persona, o null si no hay nada que decir. */
export function avisoDeFiltrada(veces) {
  if (!veces) return null;
  return `Esa contraseña aparece en ${veces.toLocaleString("es-MX")} ` +
         `filtraciones conocidas. No es que alguien sepa la tuya: es que esa ` +
         `combinación ya está en las listas que se usan para adivinar. Elige otra.`;
}
